import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./TrendingPage.css"; // Custom CSS for the Trending Page
import UpgradePopup from "./UpgradePopup";
import { auth } from "../firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import { Helmet } from "react-helmet";
import { FiBell } from "react-icons/fi";
import axios from "axios";
import { FiPlus } from "react-icons/fi";

const TrendingPage = ({ updateActiveTab, handleDisplayData }) => {
  const [data, setData] = useState([]); // State for storing the fetched data
  const [user, setUser] = useState(null);
  const [filteredData, setFilteredData] = useState([]); // State for storing filtered data
  const [loading, setLoading] = useState(true); // State for handling loading state
  const [searchTerm, setSearchTerm] = useState(""); // State for the search term
  const [sortOrder, setSortOrder] = useState(""); // State to track sort order ('latest' or 'oldest')
  const [currentPage, setCurrentPage] = useState(1); // State for managing current page
  const [upgradePopup, setUpgradePopup] = useState(false);
  const itemsPerPage = 10; // Display 10 items per page
  const navigate = useNavigate();
  const baseURL =
    process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
      ? "http://127.0.0.1:5000"
      : "https://flask-backend-52245432644.us-central1.run.app";

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, [user]);

  const addToFavorites = async (index) => {
    const userId = localStorage.getItem("databaseUid");
    try {
      const correctIndex = indexOfFirstItem + index; // Adjust index for full dataset
      const updatedTickers = [...filteredData];
      updatedTickers[correctIndex] = {
        ...updatedTickers[correctIndex],
        is_in_favorites: true,
      };
      setFilteredData(updatedTickers);
      await axios.get(
        `${baseURL}/add-to-favorites?uid=${userId}&ticker=${filteredData[
          correctIndex
        ].ticker.toUpperCase()}`
      );
    } catch (err) {
      console.log("Failed to add to favorites: " + err);
    }
  };

  const removeFromFavorites = async (index) => {
    const userId = localStorage.getItem("databaseUid");
    try {
      const correctIndex = indexOfFirstItem + index; // Adjust index for full dataset
      const updatedTickers = [...filteredData];
      updatedTickers[correctIndex] = {
        ...updatedTickers[correctIndex],
        is_in_favorites: false,
      };
      setFilteredData(updatedTickers);
      await axios.get(
        `${baseURL}/remove-from-favorites?uid=${userId}&ticker=${filteredData[
          correctIndex
        ].ticker.toUpperCase()}`
      );
    } catch (err) {
      console.log("Failed to remove from favorites: " + err);
    }
  };

  useEffect(() => {
    // Fetch trending data first
    const fetchTrendingData = async () => {
      try {
        // Try to get cached data first
        const cachedData = localStorage.getItem("trendingData");
        const cachedTimestamp = localStorage.getItem("trendingDataTimestamp");
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

        // If we have cached data and it's not too old, use it immediately
        if (cachedData && cachedTimestamp) {
          const isExpired =
            Date.now() - parseInt(cachedTimestamp) > CACHE_DURATION;
          if (!isExpired) {
            const parsedData = JSON.parse(cachedData);
            setData(parsedData);
            setFilteredData(parsedData);
            setLoading(false);
          }
        }

        // Fetch fresh data
        const response = await fetch(`${baseURL}/trending`);
        const result = await response.json();
        let trending_tickers = result.trending_tickers;
        // Initialize all tickers as not favorites
        trending_tickers = trending_tickers.map((ticker) => ({
          ...ticker,
          is_in_favorites: false,
        }));

        // Update cache
        localStorage.setItem("trendingData", JSON.stringify(trending_tickers));
        localStorage.setItem("trendingDataTimestamp", Date.now().toString());

        setData(trending_tickers);
        setFilteredData(trending_tickers);
        setLoading(false);

        // Then fetch favorites and update the status
        const userId = localStorage.getItem("databaseUid");
        const favorites_response = await axios.get(`${baseURL}/get-favorites`, {
          params: { uid: userId },
          headers: {
            "Content-Type": "application/json",
          },
        });
        const fetchedData = favorites_response.data;
        let user_favorites = [];
        for (let entry of fetchedData.favorites) {
          // If entry is an array, use entry[0] as the ticker
          user_favorites.push(Array.isArray(entry) ? entry[0] : entry.ticker);
        }

        // Update the favorite status
        const updatedTickers = trending_tickers.map((ticker) => ({
          ...ticker,
          is_in_favorites: user_favorites.includes(ticker.ticker),
        }));

        // Update cache with favorites status
        localStorage.setItem("trendingData", JSON.stringify(updatedTickers));

        setData(updatedTickers);
        setFilteredData(updatedTickers);
      } catch (err) {
        console.error("Error fetching data", err);
        setLoading(false);
      }
    };

    fetchTrendingData();

    // Cleanup function to handle component unmount
    return () => {
      // Optional: Clear cache if needed
      // localStorage.removeItem('trendingData');
      // localStorage.removeItem('trendingDataTimestamp');
    };
  }, []);

  // Update search filter to match new data structure
  useEffect(() => {
    const searchResults = data.filter((entry) =>
      entry.ticker.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(searchResults);
    setCurrentPage(1); // Reset to page 1 on search
  }, [searchTerm, data]);

  // Update sort effect to use new date format
  useEffect(() => {
    if (sortOrder) {
      const sortedData = [...filteredData].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return sortOrder === "latest" ? dateB - dateA : dateA - dateB;
      });
      setFilteredData(sortedData);
    }
  }, [sortOrder, filteredData]);

  // Handle pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredData.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const handleAnalyze = (ticker) => {
    updateActiveTab("analyze");
    navigate(`/analyze?t=${ticker}&source=trending`);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Adjust for timezone offset to get the correct local date
    const localDate = new Date(
      date.getTime() + date.getTimezoneOffset() * 60000
    );
    return localDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div>
      <Helmet>
        <title>Trending AI-based Stock Analyses Today</title>
        <meta
          name="description"
          content="Stay updated on top movers in the market. Explore trending stocks and real-time AI insights with MarketCrunch AI"
        />
      </Helmet>
      <UpgradePopup
        isOpen={upgradePopup}
        handleClose={() => setUpgradePopup(false)}
      />
      <div className="trending-page-container">
        <div className="trending-container">
          {/* Inside the box: Header, Search, Filter */}
          <div
            className="header-row"
            style={{ margin: 0, padding: 0, paddingBottom: 20 }}
          >
            <div className="right-side-controls">
              <input
                type="text"
                className="search-bar"
                placeholder="Search by ticker symbol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {/* Dropdown for sorting with "Sort By" always as a placeholder */}
              <select
                className="sort-dropdown"
                value={sortOrder}
                onChange={handleSortChange}
              >
                <option value="latest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>

          <div
            className="trending-table-container"
            style={{
              margin: "0 auto",
              overflowX: "auto",
            }}
          >
            <table
              className="trending-table"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                borderSpacing: 0,
                tableLayout: "fixed",
                fontSize: window.innerWidth <= 480 ? "14px" : "16px",
              }}
            >
              <thead>
                <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                  <th
                    style={{
                      padding: "20px",
                      width: "25%",
                      textAlign: "center",
                      color: "#1D3557",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Ticker
                  </th>
                  <th
                    style={{
                      padding: "20px",
                      width: "25%",
                      textAlign: "center",
                      color: "#1D3557",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Views
                  </th>
                  <th
                    style={{
                      padding: "20px",
                      width: "25%",
                      textAlign: "center",
                      color: "#1D3557",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                    }}
                  ></th>
                  <th
                    style={{
                      padding: "20px",
                      width: "25%",
                      textAlign: "center",
                      color: "#1D3557",
                      fontWeight: "600",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Prediction Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <td
                      colSpan="4"
                      style={{
                        textAlign: "center",
                        padding: window.innerWidth <= 480 ? "8px" : "12px 16px",
                      }}
                    >
                      Loading data...
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <td
                      colSpan="4"
                      style={{
                        textAlign: "center",
                        padding: window.innerWidth <= 480 ? "8px" : "12px 16px",
                      }}
                    >
                      No trending data found
                    </td>
                  </tr>
                ) : (
                  currentData.map((entry, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: "1px solid #e0e0e0" }}
                    >
                      <td
                        style={{
                          padding: "20px",
                          width: "25%",
                          textAlign: "center",
                          color: "#1D3557",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: window.innerWidth <= 480 ? "4px" : "8px",
                            textAlign: "left",
                            marginLeft: window.innerWidth <= 480 ? "0" : "0",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          {entry.ticker}
                          <button
                            onClick={() =>
                              entry.is_in_favorites
                                ? removeFromFavorites(index)
                                : addToFavorites(index)
                            }
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                              verticalAlign: "middle",
                            }}
                            aria-label={
                              entry.is_in_favorites
                                ? "Remove from favorites"
                                : "Add to favorites"
                            }
                          >
                            {entry.is_in_favorites ? (
                              <FiBell
                              size={window.innerWidth <= 480 ? 14 : 16}
                              style={{
                                color: "#FF4C61",   // stroke color
                                fill: "#FF4C61",    // solid fill
                              }}
                            />
                            ) : (
                              <FiBell
                                size={window.innerWidth <= 480 ? 14 : 16}
                                style={{
                                  color: "rgba(224, 88, 88, 0.75)",   // stroke color
                                  fill: "none",       // no fill
                                }}
                              />
                            )}
                          </button>
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "20px",
                          width: "25%",
                          textAlign: "center",
                          color: "#1D3557",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {entry.users && entry.users.length > 0 ? (
                          <span>
                            {(() => {
                              // Filter out usernames containing IP addresses and specific names
                              const validUsers = entry.users.filter(
                                (user) =>
                                  !user.match(/\b(?:\d{1,3}\.){3}\d{1,3}\b/) &&
                                  !user.includes("Ashim") &&
                                  !user.includes("Bhushan")
                              );
                              if (validUsers.length > 0) {
                                // Format the username to show first name and last initial
                                const nameParts = validUsers[0]
                                  .trim()
                                  .split(" ");
                                const formattedName =
                                  nameParts.length > 1
                                    ? `${nameParts[0]} ${
                                        nameParts[nameParts.length - 1][0]
                                      }.`
                                    : nameParts[0];
                                return (
                                  <>
                                    {formattedName}
                                    {entry.views > 1 && ` +${entry.views - 1}`}
                                  </>
                                );
                              }
                              // If no valid names to show, just show the total views
                              return `${entry.views} view${
                                entry.views > 1 ? "s" : ""
                              }`;
                            })()}
                          </span>
                        ) : (
                          entry.views
                        )}
                      </td>
                      <td
                        style={{
                          padding: "20px",
                          width: "25%",
                          textAlign: "center",
                          color: "#1D3557",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        <button
                          className="analyze-button"
                          onClick={() => handleAnalyze(entry.ticker)}
                          style={{
                            backgroundColor: "#139C8D",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            padding:
                              window.innerWidth <= 480
                                ? "6px 12px"
                                : "8px 16px",
                            cursor: "pointer",
                            fontWeight: "500",
                            fontFamily: "Inter, sans-serif",
                            whiteSpace: "nowrap",
                            fontSize:
                              window.innerWidth <= 480 ? "12px" : "14px",
                          }}
                        >
                          Analyze
                        </button>
                      </td>
                      <td
                        style={{
                          padding: "20px",
                          width: "25%",
                          textAlign: "center",
                          color: "#1D3557",
                          fontFamily: "Inter, sans-serif",
                        }}
                      >
                        {entry.date.split("/")[0]}/{entry.date.split("/")[1]}/
                        {entry.date.split("/")[2].slice(2)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <span>
              Showing {indexOfFirstItem + 1} to{" "}
              {indexOfLastItem < filteredData.length
                ? indexOfLastItem
                : filteredData.length}{" "}
              of {filteredData.length} entries
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <i className="fas fa-angle-left"></i>
              </button>
              <button className="page-btn active">{currentPage}</button>
              <button
                className="page-btn"
                onClick={handleNextPage}
                disabled={
                  currentPage >= Math.ceil(filteredData.length / itemsPerPage)
                }
              >
                <i className="fas fa-angle-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendingPage;
